import { useTranslation } from "react-i18next"
import type { PromptSuggestions } from "@/api/adHocCommands"

/**
 * English mode for the agent FTUE (hero, category cards and prompt
 * suggestions), toggled by a PL/EN switch at the bottom of the agent room.
 * The suggestions arrive from the assistant backend in Polish, so we
 * translate them client-side with the dictionary below — no backend deploy
 * needed. Unknown strings pass through unchanged, so a new/edited backend
 * prompt degrades to Polish instead of breaking.
 */

/** Vhosts where the PL/EN switch is offered at all. */
export const ENGLISH_TOGGLE_VHOSTS = ["demo"]

// Exact-match PL → EN dictionary for every string served by
// get_prompt_suggestions (source of truth: slickagent-pi subagents'
// promptCategories + PROMPT_CATEGORIES catalog). `#` is the driver-mention
// placeholder and must stay in the translated text.
const TRANSLATIONS: Record<string, string> = {
  // --- fleet ---
  "Co się dzieje z flotą?": "What's happening with the fleet?",
  "Status, spóźnienia, postoje — bez klikania po systemach":
    "Status, delays, stops — without clicking through systems",
  "Pokaż raport floty": "Show the fleet report",
  "Którzy kierowcy są dzisiaj spóźnieni?": "Which drivers are late today?",
  "Gdzie jest # i co teraz robi?":
    "Where is # and what are they doing right now?",
  "Wysyłaj mi poranny raport floty codziennie o 7 rano":
    "Send me a morning fleet report every day at 7 AM",

  // --- tacho ---
  "Czasy pracy i tacho": "Working time and tacho",
  "Komu ile zostało, pauzy, czy zdąży dojechać":
    "Who has how much left, breaks, will they make it in time",
  "Ile czasu jazdy zostało każdemu z moich kierowców?":
    "How much driving time does each of my drivers have left?",
  "Czy # zdąży jutro dojechać do bazy przed końcem czasu pracy?":
    "Will # make it back to base tomorrow before running out of working time?",
  "Kto z moich kierowców musi zrobić w weekend długą pauzę?":
    "Which of my drivers has to take a long rest this weekend?",
  "Którzy kierowcy mają jeszcze co najmniej 4 godziny jazdy?":
    "Which drivers still have at least 4 hours of driving left?",
  "Ile # zostało z dwutygodniowego limitu?":
    "How much of the two-week limit does # have left?",
  "Kto musi zrobić pauzę w ciągu najbliższej godziny?":
    "Who has to take a break within the next hour?",

  // --- routes ---
  "Trasy, myto i zakazy": "Routes, tolls and bans",
  "Trasa pod gabaryt auta, koszty, zakazy ruchu":
    "Routes for your truck's size, costs, driving bans",
  "Wyznacz trasę dla 40-tonowej ciężarówki z Nasielska do Wrocławia, ul. Browarna 6":
    "Plan a route for a 40-ton truck from Nasielsk to Wrocław, Browarna 6",
  "Ile wyniosą opłaty drogowe na trasie Poznań → Praga?":
    "How much are the tolls on the Poznań → Prague route?",
  "Jakie zakazy ruchu obowiązują w Austrii w najbliższy weekend?":
    "What driving bans are in force in Austria this weekend?",
  "Sprawdź, czy trasa KR12345 nie łamie zakazów w niedzielę":
    "Check if the KR12345 route breaks any Sunday driving bans",
  "Pokaż tę trasę na mapie i daj link dla kierowcy":
    "Show this route on the map and give me a link for the driver",
  "Wyznacz trasę do Mediolanu omijającą Szwajcarię":
    "Plan a route to Milan avoiding Switzerland",

  // --- comms ---
  "Komunikacja z kierowcami": "Driver communication",
  "Wiadomości, czaty bez odpowiedzi, wyszukiwanie":
    "Messages, unanswered chats, search",
  "Wyślij wiadomość do wszystkich moich kierowców":
    "Send a message to all my drivers",
  "Które czaty czekają na odpowiedź dyspozytora?":
    "Which chats are waiting for a dispatcher's reply?",
  "Czy któryś kierowca zgłaszał jakieś awarie w ostatnich 24h?":
    "Did any driver report a breakdown in the last 24h?",
  "Napisz po polsku, ukraińsku i rosyjsku, że w przyszłym tygodniu jestem na urlopie, i wyślij do moich aut":
    "Write in Polish, Ukrainian and Russian that I'm on vacation next week, and send it to my trucks",
  "Wyślij wiadomość do aut: #": "Send a message to trucks: #",
  "Kiedy # ostatnio się odezwał?": "When did # last check in?",

  // --- documents ---
  "Dokumenty i CMR": "Documents and CMR",
  "Co wpłynęło, czego brakuje, dopasowanie do zleceń":
    "What came in, what's missing, matching to orders",
  "Pokaż wszystkie CMR z dzisiaj": "Show all CMRs from today",
  "Jakie dokumenty wysłał dzisiaj #?": "What documents did # send today?",
  "Dopasuj ten CMR do zlecenia transportowego":
    "Match this CMR to a transport order",

  // --- fuel ---
  "Paliwo i tankowania": "Fuel and refueling",
  "Poziomy, historia tankowań, najtańsza stacja, alerty":
    "Levels, refueling history, cheapest station, alerts",
  "Kto ma poniżej 20% paliwa?": "Who is below 20% fuel?",
  "Pokaż najbliższą tanią stację dla #": "Show the nearest cheap station for #",
  "Pokaż dzisiejsze tankowania mojej floty":
    "Show today's refuelings for my fleet",

  // --- automations ---
  "Niech agent robi to za Ciebie": "Let the agent do it for you",
  "Raporty o stałej porze, przypomnienia, reakcje na zdarzenia":
    "Scheduled reports, reminders, reactions to events",
  "Gdy auto wjedzie do Polski, zapytaj kierowcę o usterki":
    "When a truck enters Poland, ask the driver about defects",
  "Przypominaj o tankowaniu co 700 km od ostatniego tankowania":
    "Remind about refueling every 700 km since the last refueling",
  "Wysyłaj mi raport floty codziennie o 7:00":
    "Send me a fleet report every day at 7:00 AM",
  "W każdy piątek rano sprawdzaj weekendowe zakazy ruchu dla moich tras i przyślij podsumowanie":
    "Every Friday morning check weekend driving bans for my routes and send a summary",
  "Sprawdzaj co wieczór, czy mamy komplet dokumentów z dzisiejszych rozładunków":
    "Check every evening whether we have all documents from today's unloadings",
  "Pokaż moje aktywne automatyzacje": "Show my active automations",
  "Dodaj przypomnienie o przeglądzie WX12345 na 15 lipca":
    "Add a reminder about the WX12345 inspection on July 15",
  "Pokaż nadchodzące przypomnienia dla floty": "Show upcoming fleet reminders",
  "Oznacz przegląd dla WB99999 jako wykonany":
    "Mark the WB99999 inspection as done",

  // --- orders ---
  "Stawki i rynek": "Rates and market",
  "Porównania, historia, średnie za km":
    "Comparisons, history, averages per km",
  "Porównaj stawki dla trasy Warszawa → Berlin z ostatnich 3 miesięcy":
    "Compare rates for the Warsaw → Berlin route from the last 3 months",
  "Pokaż historyczne zlecenia na trasie Poznań → Hamburg":
    "Show historical orders on the Poznań → Hamburg route",
  "Jaka była średnia stawka za km w zeszłym tygodniu?":
    "What was the average rate per km last week?",
  "Znajdź zlecenia z trasy do Holandii z ostatnich 30 dni":
    "Find orders to the Netherlands from the last 30 days",
  "Porównaj ceny dla tej trasy": "Compare prices for this route",

  // --- crm ---
  "Leady, taski, kontakty, okazje": "Leads, tasks, contacts, opportunities",
  "Pokaż otwarte leady w Close CRM": "Show open leads in Close CRM",
  "Jakie mamy nadchodzące taski w CRM na ten tydzień?":
    "What CRM tasks do we have coming up this week?",
  "Znajdź kontakt o nazwisku Kowalski": "Find a contact named Kowalski",
  "Pokaż ostatnio zaktualizowane okazje sprzedażowe":
    "Show recently updated sales opportunities",
}

/** Translate one backend-served string; unknown strings pass through. */
export function translateFtueText(text: string): string {
  return TRANSLATIONS[text] ?? text
}

/** Translate the whole get_prompt_suggestions payload to English. */
export function translatePromptSuggestions(
  suggestions: PromptSuggestions,
): PromptSuggestions {
  return {
    popular: suggestions.popular.map(translateFtueText),
    groups: suggestions.groups.map((group) => ({
      ...group,
      title: translateFtueText(group.title),
      shortDescription: translateFtueText(group.shortDescription),
      prompts: group.prompts.map(translateFtueText),
    })),
  }
}

/**
 * `t` for the FTUE's own labels. The demo switch wins over the UI language:
 * its EN mode shows English and its PL mode Polish (the switch's default, as
 * the backend serves Polish). Without the switch the labels follow the UI
 * language like the rest.
 */
export function useFtueT(english: boolean, polish = false) {
  const { t, i18n } = useTranslation()
  if (english) return i18n.getFixedT("en")
  return polish ? i18n.getFixedT("pl") : t
}
